import { useEffect, useRef, useState } from "react";
import { useNavigate } from "@tanstack/react-router";
import { claimGuestLink, ApiError } from "@/lib/api";

/**
 * `/guest#{token}` — guest share-link landing (private-beta Phase 4).
 *
 * The link a coach texts to a grandparent. On mount it POSTs the token to
 * `/api/guest/claim` (body, never a query string); the server validates
 * the link and answers with an HttpOnly guest cookie plus the team to
 * watch, and this page forwards straight to `/teams/{slug}/live`. From
 * there the private-team gates recognize the cookie on every request —
 * no account, nothing to fill in.
 *
 * The token rides the URL **fragment**, not a path segment: fragments are
 * never transmitted to the server, so a guest link can't be harvested
 * from an access log the way `/guest/<token>` was (uvicorn, Caddy and the
 * tunnel all record request paths verbatim, and this token is a REUSABLE
 * credential valid for up to 7 days — unlike a single-use invite token).
 * Same reasoning as the token-in-POST-body rule on the claim endpoint.
 *
 * Claiming is idempotent and creates no records, so the on-mount
 * auto-claim is fine here — unlike invite accept, which now requires
 * explicit consent. Following the link IS the whole intent.
 *
 * Unhappy paths mirror the invite-token shapes:
 *   - 404 → invalid or revoked (same message — no existence probing)
 *   - 410 → expired
 */
function tokenFromHash(): string {
  if (typeof window === "undefined") return "";
  const raw = window.location.hash.replace(/^#/, "").trim();
  try {
    return decodeURIComponent(raw);
  } catch {
    // A malformed percent-escape is a malformed link — treat it as absent
    // and let the claim call surface the standard "invalid link" copy.
    return raw;
  }
}

export function GuestClaimPage() {
  const token = tokenFromHash();
  const navigate = useNavigate({ from: "/guest" });
  const [error, setError] = useState<string | null>(null);
  const [attempt, setAttempt] = useState(0);
  const started = useRef(false);

  useEffect(() => {
    if (started.current) return;
    started.current = true;
    if (!token) {
      // No fragment at all — a truncated paste, or a link mangled by a
      // client that strips fragments. Same copy as an unknown token so
      // the page never distinguishes "no link" from "dead link".
      setError(
        "This viewing link is invalid or has been turned off. Ask your team contact for a fresh one.",
      );
      return;
    }
    claimGuestLink(token)
      .then((resp) => {
        void navigate({
          to: "/teams/$teamSlug/live",
          params: { teamSlug: resp.team_slug },
          replace: true,
        });
      })
      .catch((err) => {
        if (err instanceof ApiError && err.status === 410) {
          setError(
            "This viewing link has expired. Ask your team contact for a fresh one.",
          );
        } else if (err instanceof ApiError && err.status === 404) {
          setError(
            "This viewing link is invalid or has been turned off. Ask your team contact for a fresh one.",
          );
        } else {
          setError("Something went wrong. Try the link again in a moment.");
        }
      });
    // `attempt` is the retry trigger: bumping it re-runs this effect for
    // the SAME token. Without it a transient network blip latched the
    // one-shot guard and the only way back was a hard reload.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [attempt]);

  return (
    <main
      data-testid="guest-claim"
      className="min-h-screen bg-canvas flex items-center justify-center p-4"
    >
      <section className="w-full max-w-md rounded border border-rule bg-surface p-6 flex flex-col gap-3">
        <h1 className="text-xl font-display tracking-tight text-ink">
          Watch the team
        </h1>
        {error ? (
          <div className="flex flex-col items-start gap-3">
            <p
              data-testid="guest-claim-error"
              className="text-destructive text-sm"
            >
              {error}
            </p>
            <button
              type="button"
              onClick={() => {
                started.current = false;
                setError(null);
                setAttempt((n) => n + 1);
              }}
              data-testid="guest-claim-retry"
              className="rounded border border-rule px-3 py-2 font-display text-sm uppercase tracking-wider text-ink-muted transition-colors hover:bg-field hover:text-ink"
            >
              Try again
            </button>
          </div>
        ) : (
          <p
            data-testid="guest-claim-loading"
            className="text-ink-muted text-sm"
          >
            Checking your viewing link…
          </p>
        )}
      </section>
    </main>
  );
}
